import React, { useState, useEffect } from 'react'
import { useDarkMode } from '../App'
import { BookEditModal } from './BookEditModal'
import { BOOK_MANAGEMENT_API_BASE } from '../lib/api'

interface Book {
  slug: string
  title: string
  authors: string[]
  year: number
  license: string
  type: 'book' | 'paper'
  coverUrl: string
  fileSize: string
  fileType: string
  description: string
}

interface BookManagementProps {
  onError?: (error: string) => void
  onSuccess?: (message: string) => void
}

export default function BookManagement({ onError, onSuccess }: BookManagementProps) {
  const { isDark } = useDarkMode()
  const [books, setBooks] = useState<Book[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<'all' | 'book' | 'paper'>('all')
  const [selectedBook, setSelectedBook] = useState<Book | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const fetchBooks = async (page = 1, search = searchTerm, type = filterType) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20',
        ...(search && { search }),
        ...(type !== 'all' && { type })
      })

      const response = await fetch(`${BOOK_MANAGEMENT_API_BASE}/api/get-books?${params}`)
      if (!response.ok) {
        throw new Error('Failed to fetch books')
      }

      const data = await response.json()
      setBooks(data.books)
      setTotalPages(data.pagination.total_pages)
      setCurrentPage(data.pagination.page)
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Failed to load books')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBooks()
  }, [])

  const handleSearch = () => {
    setCurrentPage(1)
    fetchBooks(1, searchTerm, filterType)
  }

  const handleEdit = (book: Book) => {
    setSelectedBook(book)
    setShowEditModal(true)
  }

  const handleDelete = async (book: Book) => {
    if (!confirm(`Are you sure you want to delete "${book.title}"? This will also delete the associated files.`)) {
      return
    }

    try {
      const response = await fetch(`${BOOK_MANAGEMENT_API_BASE}/api/delete-book/${book.slug}?delete_files=true`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete book')
      }

      const result = await response.json()
      onSuccess?.(`Book "${book.title}" has been deleted. Manual library.ts update required.`)
      
      // Refresh the books list
      fetchBooks(currentPage, searchTerm, filterType)
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Delete failed')
    }
  }

  const handleSaveBook = (updatedBook: Book) => {
    onSuccess?.(`Book "${updatedBook.title}" has been updated. Manual library.ts update required.`)
    
    // Update the book in the local state
    setBooks(books.map(book => 
      book.slug === selectedBook?.slug ? updatedBook : book
    ))
  }

  const handlePageChange = (page: number) => {
    fetchBooks(page, searchTerm, filterType)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className={`text-2xl font-bold ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>
          Book Management
        </h2>
        <div className={`px-3 py-1 rounded-full text-sm ${
          isDark ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
        }`}>
          {books.length} books
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search books by title, author, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className={`w-full px-4 py-2 border rounded-lg ${
              isDark 
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as 'all' | 'book' | 'paper')}
          className={`px-4 py-2 border rounded-lg ${
            isDark 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        >
          <option value="all">All Types</option>
          <option value="book">Books</option>
          <option value="paper">Papers</option>
        </select>
        <button
          onClick={handleSearch}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Search
        </button>
      </div>

      {/* Books List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading books...
          </p>
        </div>
      ) : books.length === 0 ? (
        <div className={`text-center py-8 ${
          isDark ? 'text-gray-300' : 'text-gray-600'
        }`}>
          <p>No books found matching your criteria.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {books.map((book) => (
            <div key={book.slug} className={`p-4 rounded-lg border ${
              isDark 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4">
                    <img
                      src={book.coverUrl}
                      alt={book.title}
                      className="w-12 h-16 object-cover rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-book.png'
                      }}
                    />
                    <div className="flex-1">
                      <h3 className={`font-semibold text-lg ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {book.title}
                      </h3>
                      <p className={`text-sm ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {book.authors.join(', ')} • {book.year}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-1 text-xs rounded ${
                          book.type === 'book' 
                            ? isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'
                            : isDark ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {book.type}
                        </span>
                        <span className={`text-xs ${
                          isDark ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {book.fileSize} • {book.license}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className={`mt-2 text-sm ${
                    isDark ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {book.description.length > 200 
                      ? `${book.description.substring(0, 200)}...` 
                      : book.description
                    }
                  </p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(book)}
                    className={`px-3 py-1 text-sm rounded ${
                      isDark 
                        ? 'bg-blue-600 text-white hover:bg-blue-500' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(book)}
                    className={`px-3 py-1 text-sm rounded ${
                      isDark 
                        ? 'bg-red-600 text-white hover:bg-red-500' 
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : isDark
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
            }`}
          >
            Previous
          </button>
          
          <span className={`px-4 py-1 ${
            isDark ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : isDark
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border'
            }`}
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Modal */}
      <BookEditModal
        book={selectedBook}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedBook(null)
        }}
        onSave={handleSaveBook}
        onError={onError || (() => {})}
      />
    </div>
  )
}